import React from 'react';
import {
  Modal,
  Backdrop,
  Fade,
  Box,
  Typography,
  TextField,
  Button,
  Autocomplete,
  Alert,
} from '@mui/material';
import { UserDto } from '@cloudcost/shared-types/users';

interface DepartmentFormData {
  id?: number;
  name: string;
  id_owner: number | null;
}

interface CreateDepartmentModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  onDelete: () =>void;
  users: UserDto[];
  formData: DepartmentFormData;
  setFormData: (data: DepartmentFormData) => void;
  alertOwner: boolean;
  isEditMode?: boolean;
}

export const CreateDepartmentModal: React.FC<CreateDepartmentModalProps> = ({
  open,
  onClose,
  onSubmit,
  onDelete,
  users,
  formData,
  setFormData,
  alertOwner,
  isEditMode = false,
}) => {
  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      slots={{
        backdrop: Backdrop,
      }}
      slotProps={{
        backdrop: {
          timeout: 500,
        },
      }}>
      <Fade in={open}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            boxShadow: '0 12px 40px rgba(16,35,58,.16)',
            p: 4,
            borderRadius: '14px',
          }}>
          <Typography variant="h6" component="h2" sx={{ mb: 3 }}>
            {isEditMode ? 'Edit Department' : 'Create Department'}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              id="department-name"
              label="Department Name"
              variant="outlined"
              fullWidth
              value={formData.name ?? ''}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              inputProps={{ 'data-testid': 'department-creation-name' }}
            />

            <Autocomplete
              disablePortal
              id="combo-box-owners"
              options={users}
              value={
                users.find((row) => row.id === formData.id_owner) ?? null
              }
              getOptionLabel={(option) => option.username}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Department Owner"
                  variant="outlined"
                  inputProps={{ ...params.inputProps, 'data-testid': 'department-creation-owner' }}
                />
              )}
              onChange={(event, value) => {
                if (value) {
                  setFormData({ ...formData, id_owner: value.id });
                }
              }}
            />

            {alertOwner && (
              <Alert severity="error">A Department must have an Owner. Select one from the list or add a new user account first.</Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button
                variant="contained"
                onClick={onSubmit}
                color="primary"
                fullWidth
                data-testid="department-creation-submit">
                {isEditMode ? 'Update Department' : 'Create Department'}
              </Button>
              {isEditMode && (
                <Button
                  variant="contained"
                  onClick={onDelete}   // Better use a delete handler instead of onSubmit
                  color="error"
                  fullWidth
                  data-testid="department-edit-delete">
                  Delete Department
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};